import type {
  Command,
  Dependencias,
} from '@/features/proposta/entities/Comprador/form-to-listagem/comprador-listagem-command-factory'
import {
  transformarCompradorEVinculosEmClienteJuridicoECliente,
  transformarCompradorFormParaCompradorPJ,
} from '@/features/proposta/entities/Comprador/form-to-listagem/mapper-comprador-pj-para-listagem'
import type {
  TClientePessoaFisicaPropostaSchema,
  TClientePessoaJuridicaPropostaSchema,
  TProcuradorPropostaSchema,
} from '@/features/proposta/schemas'
import type { CompradorPessoaJuridicaSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pj.schema'

export class AdicionarPessoaJuridicaCommand implements Command {
  constructor(
    private readonly comprador: CompradorPessoaJuridicaSchemaInput,
    private readonly clientes: () => TClientePessoaFisicaPropostaSchema[],
    private readonly procuradores: () => TProcuradorPropostaSchema[],
    private readonly deps: Dependencias,
  ) {}

  executar() {
    const clientesJuridicoEPessoaFisica = transformarCompradorEVinculosEmClienteJuridicoECliente(this.comprador)
    this.deps.adicionarClienteJuridico(clientesJuridicoEPessoaFisica.clientePJ)

    const indexCliente = this.clientes().findIndex(
      (cliente) => cliente.id === clientesJuridicoEPessoaFisica.clientePF.id,
    )
    const cliente = clientesJuridicoEPessoaFisica.clientePF
    if (indexCliente === -1) {
      this.deps.adicionarCliente(cliente)
    } else {
      this.deps.atualizarCliente(indexCliente, cliente)
    }

    const comprador = transformarCompradorFormParaCompradorPJ(this.comprador)
    if (this.comprador.responsavel?.tipo === 'Procurador') {
      const indexProcurador = this.procuradores().findIndex(
        (c) => c.procuradorId === this.comprador.responsavel?.dados.id,
      )
      if (indexProcurador === -1) {
        this.deps.adicionarProcurador({
          clientesIds: [clientesJuridicoEPessoaFisica.clientePJ.id],
          procuradorId: this.comprador.responsavel.dados.id!,
        })
      } else {
        this.deps.atualizarProcurador(indexProcurador, {
          clientesIds: [
            ...(this.procuradores()[indexProcurador]?.clientesIds ?? []),
            clientesJuridicoEPessoaFisica.clientePJ.id,
          ],
          procuradorId: this.comprador.responsavel.dados.id!,
        })
      }
    }
    this.deps.adicionarComprador(comprador)
  }
}

export class AtualizarPessoaJuridicaCommand implements Command {
  constructor(
    private readonly comprador: CompradorPessoaJuridicaSchemaInput,
    private readonly clientesPJ: () => TClientePessoaJuridicaPropostaSchema[],
    private readonly clientesPF: () => TClientePessoaFisicaPropostaSchema[],
    private readonly deps: Dependencias,
  ) {}

  atualizarRepresentante() {
    const clientes = this.clientesPF()
    const representanteIndex = clientes.findIndex((cliente) => cliente.id === this.comprador.responsavel?.dados.id)
    if (!clientes[representanteIndex]) return
    this.deps.atualizarCliente(representanteIndex, {
      ...clientes[representanteIndex],
      nacionalidadeId: this.comprador.responsavel!.dados.nacionalidadeId,
      profissaoId: this.comprador.responsavel!.dados.profissaoId,
      estadoCivil: this.comprador.responsavel!.estadoCivil.status,
      regimeCasamento: this.comprador.responsavel!.estadoCivil.regimeCasamento,
      possuiUniaoEstavel: this.comprador.responsavel!.estadoCivil.uniaoEstavel
        ? this.comprador.responsavel!.estadoCivil.uniaoEstavel === 'sim'
        : null,
      nome: this.comprador.responsavel!.dados.nome,
      email: this.comprador.responsavel!.contato.email!,
      telefone: {
        numero: this.comprador.responsavel!.contato.telefone!.numero.replaceAll(/\D/g, '') ?? '',
        origem: this.comprador.responsavel!.contato.telefone!.origem!,
        principal: true,
      },
    })
  }

  executar() {
    const clientesJuridico = this.clientesPJ()
    const indexCliente = clientesJuridico.findIndex((cliente) => cliente.id === this.comprador.dados.id)
    if (!clientesJuridico[indexCliente])
      throw new Error(`Cliente Jurídico com ID ${this.comprador.dados.id} não encontrado.`)
    if (this.comprador.responsavel?.fluxoIndependente === 'Representante') {
      return this.atualizarRepresentante()
    }

    this.deps.atualizarClienteJuridico(indexCliente, {
      ...clientesJuridico[indexCliente],
      cnpj: this.comprador.dados.cnpj,
      email: this.comprador.dados.email,
      razaoSocial: this.comprador.dados.razaoSocial,
      telefone: {
        numero: this.comprador.dados.telefone!.numero.replaceAll(/\D/g, ''),
        origem: this.comprador.dados.telefone!.origem!,
        principal: true,
      },
    })
  }
}
